// The session verifier seam.
//
// BUILD-ORDER requires local verification with `@you-identity/session`
// (`createSessionVerifier({ publicKey, sessionVersion })`, then `verifyValue`),
// verify only, failing closed as not_configured when no public key is held.
//
// That package is on GitHub Packages and is not installable in this repo yet
// (ESCALATIONS.md B8), so this module is the seam it will slot into. Everything
// downstream depends on the `SessionVerifier` interface below, never on the
// package directly, so wiring the real verifier when B8 clears touches only
// `getConfiguredVerifier` and nothing else. This is the "swap in without a
// refactor" contract BUILD-ORDER asks for, applied to the session layer.

import { SESSION_PUBLIC_KEY, SESSION_VERSION } from '../config'

/** Verified claim set. Shape is SESSION-CONTRACT section 10 (spec 3.1). */
export interface SessionClaims {
  readonly sub: string
  readonly iat: number
  readonly exp: number
  readonly sv: number
}

export type VerifyResult =
  | { readonly ok: true; readonly claims: SessionClaims }
  | { readonly ok: false; readonly reason: 'expired' | 'invalid' | 'revoked' }

/** The one method the app depends on. Matches `@you-identity/session`. */
export interface SessionVerifier {
  verifyValue(token: string): Promise<VerifyResult>
}

/**
 * Return a configured verifier, or null when the app is not_configured.
 *
 * Null is the live answer today: no public key is provisioned, so there is
 * nothing to verify with and the app must say so rather than pretend. When B8
 * clears and a key is configured, this wraps
 * `createSessionVerifier({ publicKey, sessionVersion })` from
 * `@you-identity/session` and returns a real verifier. It never returns a
 * verifier that trusts without checking.
 */
export function getConfiguredVerifier(): SessionVerifier | null {
  if (SESSION_PUBLIC_KEY === undefined || SESSION_PUBLIC_KEY.length === 0) {
    return null
  }
  // B8: with the package installable, this becomes:
  //   const v = createSessionVerifier({
  //     publicKey: SESSION_PUBLIC_KEY, sessionVersion: SESSION_VERSION,
  //   })
  //   return { verifyValue: (t) => v.verifyValue(t) }
  // Until then a configured-but-unavailable verifier is a build error, not a
  // silent pass, so it is left unreachable rather than stubbed to succeed.
  void SESSION_VERSION
  throw new Error(
    'session verifier not linked: @you-identity/session unavailable (B8)',
  )
}
